import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Search, LayoutDashboard, FolderKanban, CheckSquare, Timer,
  Columns3, BarChart3, Users, Settings, Plus, ArrowRight,
  FileText, Target, Clock, CalendarDays, Hash, Zap,
  MessageCircle, Command,
} from 'lucide-react';
import { useListTasksQuery } from '../../api/taskApi';

/**
 * CommandPalette — Global search & action launcher (Cmd+K / Ctrl+K)
 *
 * Features:
 *   Fuzzy search across navigation, actions, and tasks
 *   Full keyboard navigation (↑↓ Enter Esc)
 *   Categorized results: Navigation, Actions, Recent Tasks
 *   Glass-blur overlay with smooth animations
 *   Shortcut hints per action
 */

// ── Static navigation items ──
const NAV_ITEMS = [
  { id: 'nav-dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/', category: 'Navigate' },
  { id: 'nav-projects', label: 'Projects', icon: FolderKanban, path: '/projects', category: 'Navigate' },
  { id: 'nav-tasks', label: 'Tasks', icon: CheckSquare, path: '/tasks', category: 'Navigate' },
  { id: 'nav-sprints', label: 'Sprints', icon: Timer, path: '/sprints', category: 'Navigate' },
  { id: 'nav-kanban', label: 'Kanban Board', icon: Columns3, path: '/kanban', category: 'Navigate' },
  { id: 'nav-gantt', label: 'Gantt Chart', icon: BarChart3, path: '/gantt', category: 'Navigate' },
  { id: 'nav-calendar', label: 'Calendar', icon: CalendarDays, path: '/calendar', category: 'Navigate' },
  { id: 'nav-timesheets', label: 'Timesheets', icon: Clock, path: '/timesheets', category: 'Navigate' },
  { id: 'nav-goals', label: 'Goals', icon: Target, path: '/goals', category: 'Navigate' },
  { id: 'nav-docs', label: 'Documents', icon: FileText, path: '/documents', category: 'Navigate' },
  // HiveLink renders outside MainLayout as a full-window app, so it opens in
  // its own tab — matching the Sidebar. Navigating here in the same tab left
  // you in an app with no Topbar and no way back.
  { id: 'nav-chat', label: 'HiveLink Chat', icon: MessageCircle, path: '/hivelink', category: 'Navigate', newTab: true },
  { id: 'nav-members', label: 'Members', icon: Users, path: '/members', category: 'Navigate' },
  { id: 'nav-settings', label: 'Settings', icon: Settings, path: '/settings', category: 'Navigate' },
];

const ACTION_ITEMS = [
  { id: 'act-new-task', label: 'Create New Task', icon: Plus, action: 'create-task', shortcut: 'N', category: 'Actions' },
  { id: 'act-new-project', label: 'Create New Project', icon: Plus, action: 'create-project', category: 'Actions' },
  { id: 'act-new-sprint', label: 'Create New Sprint', icon: Zap, action: 'create-sprint', category: 'Actions' },
];

function fuzzyMatch(text, query) {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  if (lower.includes(q)) return true;
  // Check initials match: "Kanban Board" matches "kb"
  const initials = text.split(/\s+/).map((w) => w[0]?.toLowerCase()).join('');
  return initials.includes(q);
}

const CommandPalette = ({ onAction }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Fetch tasks for search (light query)
  const wsId = localStorage.getItem('activeWorkspaceId');
  const { data: tasksData } = useListTasksQuery(
    { limit: 50, sort: '-updatedAt' },
    { skip: !open || !wsId },
  );
  const tasks = tasksData?.data || [];

  // Build search results
  const results = useMemo(() => {
    const q = query.trim();
    let items = [];

    if (!q) {
      // Show recent actions + top nav
      items = [
        ...ACTION_ITEMS,
        ...NAV_ITEMS.slice(0, 6),
        ...tasks.slice(0, 5).map((t) => ({
          id: `task-${t._id}`,
          label: `${t.taskKey || ''} ${t.title}`.trim(),
          icon: Hash,
          path: `/tasks?taskId=${t._id}`,
          category: 'Recent Tasks',
          subtitle: t.status,
        })),
      ];
    } else {
      // Filter by query
      const navMatches = NAV_ITEMS.filter((i) => fuzzyMatch(i.label, q));
      const actionMatches = ACTION_ITEMS.filter((i) => fuzzyMatch(i.label, q));
      const taskMatches = tasks
        .filter((t) => fuzzyMatch(t.title, q) || (t.taskKey && fuzzyMatch(t.taskKey, q)))
        .slice(0, 8)
        .map((t) => ({
          id: `task-${t._id}`,
          label: `${t.taskKey || ''} ${t.title}`.trim(),
          icon: Hash,
          path: `/tasks?taskId=${t._id}`,
          category: 'Tasks',
          subtitle: t.status,
        }));

      items = [...actionMatches, ...navMatches, ...taskMatches];
    }

    return items;
  }, [query, tasks]);

  // Group by category
  const grouped = useMemo(() => {
    const groups = {};
    results.forEach((item) => {
      const cat = item.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  }, [results]);

  const flatItems = results;

  // ── Keyboard shortcut to open ──
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Keep active item in view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const active = list.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSelect = useCallback((item) => {
    setOpen(false);
    if (item.path) {
      if (item.newTab) {
        window.open(item.path, '_blank', 'noopener,noreferrer');
      } else {
        navigate(item.path);
      }
    } else if (item.action) {
      onAction?.(item.action);
    }
  }, [navigate, onAction]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatItems[activeIndex]) handleSelect(flatItems[activeIndex]);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-surface-950/40 backdrop-blur-[8px] animate-modal-overlay"
        onClick={() => setOpen(false)}
      />

      {/* Panel */}
      <div className="relative flex flex-col mx-auto mt-[15vh] w-full max-w-lg animate-modal-content">
        <div className="bg-white rounded-2xl shadow-modal border border-surface-200/50 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-surface-100">
            <Search size={16} className="text-surface-500 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
              onKeyDown={handleKeyDown}
              placeholder="Search or jump to..."
              className="flex-1 text-[14px] text-surface-900 placeholder-surface-400 bg-transparent outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-2xs font-mono text-surface-500 bg-surface-100 rounded border border-surface-200">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[360px] overflow-y-auto py-1 scrollbar-thin">
            {flatItems.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Search size={24} className="mx-auto mb-2 text-surface-300" />
                <p className="text-[13px] text-surface-600">
                  No results for &ldquo;{query}&rdquo;
                </p>
              </div>
            ) : (
              Object.entries(grouped).map(([category, items]) => (
                <div key={category}>
                  <div className="px-3 pt-2 pb-1">
                    <span className="text-2xs font-semibold text-surface-500 uppercase tracking-wider">
                      {category}
                    </span>
                  </div>
                  {items.map((item) => {
                    const globalIdx = flatItems.indexOf(item);
                    const isActive = globalIdx === activeIndex;
                    const Icon = item.icon;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        data-active={isActive}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setActiveIndex(globalIdx)}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2 mx-1 rounded-lg text-left transition-colors
                          ${isActive
                            ? 'bg-primary-50 text-primary-800'
                            : 'text-surface-700 hover:bg-surface-50'
                          }
                        `}
                        style={{ width: 'calc(100% - 8px)' }}
                      >
                        <div className={`
                          w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0
                          ${isActive ? 'bg-primary-100 text-primary-600' : 'bg-surface-100 text-surface-600'}
                        `}>
                          <Icon size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium truncate">{item.label}</p>
                          {item.subtitle && (
                            <p className="text-2xs text-surface-500 truncate">{item.subtitle}</p>
                          )}
                        </div>
                        {item.shortcut && (
                          <kbd className="text-2xs font-mono text-surface-500 bg-surface-100 rounded px-1.5 py-0.5 border border-surface-200">
                            {item.shortcut}
                          </kbd>
                        )}
                        {item.path && (
                          <ArrowRight size={12} className={`flex-shrink-0 ${isActive ? 'text-primary-400' : 'text-surface-300'}`} />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-surface-100 bg-surface-50/50">
            <div className="flex items-center gap-3 text-2xs text-surface-500">
              <span className="flex items-center gap-1">
                <kbd className="inline-flex items-center justify-center w-4 h-4 bg-surface-200 rounded text-2xs">↑</kbd>
                <kbd className="inline-flex items-center justify-center w-4 h-4 bg-surface-200 rounded text-2xs">↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="inline-flex items-center justify-center min-w-[28px] h-4 px-1 bg-surface-200 rounded text-2xs">⏎</kbd>
                select
              </span>
            </div>
            <div className="flex items-center gap-1 text-2xs text-surface-500">
              <Command size={10} />
              <span>K to toggle</span>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CommandPalette;